import { useCallback, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { api } from "../api/client";
import { usePoll } from "../hooks/usePoll";
import {
  TOOLTIP_STYLE,
  insightsTheme,
  integerTicks,
  useChartPalette,
} from "./insights/theme";
import { InfoHint } from "./Tooltip";

const BUCKET_HINTS: Record<string, string> = {
  hour: "Last 24 hours, one point per hour",
  day: "Last 14 days, one point per day",
  week: "Last 10 weeks, one point per week",
  month: "Last 12 months, one point per month",
};
// What "New" means for the headline number, per bucket.
const NEW_LABEL: Record<string, string> = {
  hour: "new this hour",
  day: "new today",
  week: "new this week",
  month: "new this month",
};

// The growth charts: users / memexes / specs over time, on a switchable bucket
// and a Total↔New mode. Total = cumulative running line+area; New = per-bucket
// bars (count created in each bucket) — the velocity view. Both series ship in
// one response so the toggle is instant. Nivo + the shared Insights theme
// (std-27). Reused on the overview (platform), an org page, and a memex page.

type Bucket = "hour" | "day" | "week" | "month";
type Mode = "total" | "new";
const BUCKETS: { key: Bucket; label: string }[] = [
  { key: "hour", label: "24h" },
  { key: "day", label: "14d" },
  { key: "week", label: "10w" },
  { key: "month", label: "12m" },
];

interface Point {
  t: string;
  users: number;
  memexes: number;
  specs: number;
  usersNew: number;
  memexesNew: number;
  specsNew: number;
}
interface Timeseries {
  bucket: Bucket;
  points: Point[];
}

type Metric = "users" | "memexes" | "specs";
const NEW_KEY: Record<Metric, "usersNew" | "memexesNew" | "specsNew"> = {
  users: "usersNew",
  memexes: "memexesNew",
  specs: "specsNew",
};

function formatTick(iso: string, bucket: Bucket): string {
  const d = new Date(iso);
  if (bucket === "hour") return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (bucket === "month") return d.toLocaleDateString([], { month: "short", year: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function GrowthCharts({
  query = "",
  variant = "platform",
}: {
  query?: string;
  variant?: "platform" | "org" | "memex";
}) {
  const [bucket, setBucket] = useState<Bucket>("day");
  const [mode, setMode] = useState<Mode>("total");
  const fetcher = useCallback(
    () => api<Timeseries>(`/api/stats/timeseries?bucket=${bucket}${query ? `&${query}` : ""}`),
    [bucket, query],
  );
  const { data, error } = usePoll(fetcher);
  const points = data?.points ?? [];
  const palette = useChartPalette();

  const specsHint =
    variant === "memex"
      ? "Specs created in this memex over time."
      : variant === "org"
        ? "Specs created in this org's memexes over time."
        : "Every spec created across the whole platform (incl. personal memexes). The per-org table below won't sum to this.";
  // Users live at the platform level, not inside a memex — so on the overview the
  // headcount is ALWAYS the whole platform, immune to the personal/internal/exclude
  // filters (which only move memexes & specs). An org page scopes to its members.
  const usersHint =
    variant === "org"
      ? "Users with an active membership in this org over time."
      : "Every signed-up account on the whole platform (the same number the Live tab shows). This is always the full headcount: the Include-personal and Hide-internal switches, and the per-org chart toggles, change only the Memexes and Specs charts, never this number. So it won't move when you toggle them, and the per-org table below won't sum to it.";
  const CHARTS: Record<Metric, { title: string; color: string; testid: string; hint: string }> = {
    users: { title: "Users", color: palette.human, testid: "chart-users", hint: usersHint },
    memexes: { title: "Memexes", color: palette.accent, testid: "chart-memexes", hint: "Memexes created — including personal ones. Whole platform; the per-org table below won't sum to this." },
    specs: { title: "Specs", color: palette.verified, testid: "chart-specs", hint: specsHint },
  };
  const metrics: Metric[] =
    variant === "memex" ? ["specs"] : variant === "org" ? ["users", "specs"] : ["users", "memexes", "specs"];

  return (
    <section data-testid="growth-charts">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
          Growth
          <InfoHint text="Total = the cumulative running total over time. New = how many were created in each bucket (the velocity view). Pick a time window on the right." />
        </h2>
        <div className="flex items-center gap-4">
          <Segmented
            ariaLabel="Chart mode"
            value={mode}
            onChange={setMode}
            options={[
              { key: "total", label: "Total", testid: "mode-total", title: "Cumulative running total" },
              { key: "new", label: "New", testid: "mode-new", title: "Created in each bucket" },
            ]}
          />
          <Segmented
            ariaLabel="Time range"
            value={bucket}
            onChange={setBucket}
            options={BUCKETS.map((b) => ({ key: b.key, label: b.label, testid: `bucket-${b.key}`, title: BUCKET_HINTS[b.key] }))}
          />
        </div>
      </div>

      {error && <p className="mb-2 text-sm text-status-danger-text">Failed to load growth data.</p>}

      <div
        className={`grid grid-cols-1 gap-6 ${
          metrics.length === 3 ? "lg:grid-cols-3" : metrics.length === 2 ? "lg:grid-cols-2" : ""
        }`}
      >
        {metrics.map((m) => (
          <ChartCard
            key={m}
            title={CHARTS[m].title}
            hint={CHARTS[m].hint}
            color={CHARTS[m].color}
            points={points}
            metric={m}
            bucket={bucket}
            mode={mode}
            testid={CHARTS[m].testid}
          />
        ))}
      </div>
    </section>
  );
}

// A segmented control: one bordered container, the active option filled. Two of
// these side by side (mode + time range) read as two distinct controls rather
// than one undifferentiated row of pills.
function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string; testid: string; title?: string }[];
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex items-center rounded-lg border border-edge bg-page p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          data-testid={o.testid}
          aria-pressed={value === o.key}
          title={o.title}
          className={[
            "rounded-md px-2.5 py-1 text-xs transition-colors",
            value === o.key ? "bg-card-hover text-heading shadow-sm" : "text-secondary hover:text-primary",
          ].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ChartCard({
  title,
  hint,
  color,
  points,
  metric,
  bucket,
  mode,
  testid,
}: {
  title: string;
  hint: string;
  color: string;
  points: Point[];
  metric: Metric;
  bucket: Bucket;
  mode: Mode;
  testid: string;
}) {
  const last = points[points.length - 1];
  const newKey = NEW_KEY[metric];
  const value = mode === "total" ? (last?.[metric] ?? 0) : (last?.[newKey] ?? 0);
  const caption = mode === "total" ? "to date" : NEW_LABEL[bucket];

  const every = Math.max(1, Math.ceil(points.length / 8));
  const tickValues = points.filter((_, i) => i % every === 0).map((p) => p.t);
  const yMax = mode === "total" ? Math.max(1, ...points.map((p) => p[metric])) : Math.max(1, ...points.map((p) => p[newKey]));
  const yTicks = integerTicks(yMax);

  return (
    <div className="rounded-xl border border-edge bg-surface p-4" data-testid={`${testid}-card`}>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted">
          {title}
          <InfoHint text={hint} align="start" />
        </span>
        <span className="flex items-baseline gap-1.5">
          <span className="text-2xl font-semibold text-heading" data-testid={`${testid}-total`}>{value}</span>
          <span className="text-[11px] text-muted">{caption}</span>
        </span>
      </div>
      {/* The testid is on this STABLE wrapper (not the Nivo svg) so tests never
          race a re-render swapping the chart between line and bars. */}
      <div className="relative h-56" data-testid={testid} role="img" aria-label={`${title} over time`}>
        {points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted">No data yet</div>
        ) : mode === "total" ? (
          <ResponsiveLine
            data={[{ id: title, color, data: points.map((p) => ({ x: p.t, y: p[metric] })) }]}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: 0, max: "auto" }}
            margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
            colors={(serie) => String(serie.color)}
            theme={insightsTheme}
            curve="monotoneX"
            enablePoints={false}
            enableArea
            areaOpacity={0.12}
            lineWidth={2.5}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickValues, format: (v) => formatTick(String(v), bucket) }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: yTicks }}
            gridYValues={yTicks}
            enableGridX={false}
            enableSlices="x"
            sliceTooltip={({ slice }) => {
              const pt = slice.points[0];
              return (
                <div className="rounded-lg px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
                  <div className="mb-1 font-medium">{formatTick(String(pt?.data.x), bucket)}</div>
                  <div style={{ color }}>{String(pt?.data.y ?? 0)} {title.toLowerCase()}</div>
                </div>
              );
            }}
            animate
          />
        ) : (
          <ResponsiveBar
            data={points.map((p) => ({ t: p.t, value: p[newKey] }))}
            keys={["value"]}
            indexBy="t"
            margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
            padding={0.3}
            colors={[`${color}cc`]}
            borderRadius={2}
            theme={insightsTheme}
            enableLabel={false}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickValues, format: (v) => formatTick(String(v), bucket) }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: yTicks }}
            enableGridY
            gridYValues={yTicks}
            tooltip={({ data: d, value: v }) => (
              <div className="rounded-lg px-3 py-2 text-xs" style={TOOLTIP_STYLE}>
                <div className="mb-1 font-medium">{formatTick(String(d.t), bucket)}</div>
                <div style={{ color }}>{v} new {title.toLowerCase()}</div>
              </div>
            )}
            animate
          />
        )}
      </div>
    </div>
  );
}
